/**
 * Kiosk Store
 *
 * Stores kiosk session state: kiosk identity, visitor session data,
 * cart items, and language preference.
 */

import { create } from 'zustand';
import type { Locale } from '../i18n/translations';

export type TierName = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';

// ─── Photo Types ──────────────────────────────────────────────────────────────

export interface KioskPhoto {
  id: string;
  attractionId: string;
  attractionName?: string | null;
  capturedAt: string;
  thumbnailUrl: string | null;
  photoUrl: string;
  price: number;
}

// ─── F&B Types ────────────────────────────────────────────────────────────────

export interface FnbCartItem {
  menuItemId: string;
  name: string;
  nameId: string;
  price: number;
  quantity: number;
  variantId?: string;
  variantName?: string;
  modifiers?: Array<{ name: string; priceAdjustment: number }>;
  imageUrl?: string;
  station: string;
}

export interface FnbOrderResult {
  orderId: string;
  orderRef: string;
  status: string;
  estimatedReadyAt: string;
  items: FnbCartItem[];
  totalAmount: number;
}

export interface VisitorInfo {
  wristbandId: string;
  visitorName: string;
  creditBalance: number;
  tier: TierName;
  sessionId: string;
}

export interface CartItem {
  attractionId: string;
  attractionName: string;
  creditCost: number;
  imageUrl?: string;
}

export interface TransactionReceipt {
  transactionId: string;
  amount: number;
  paymentMethod: string;
  timestamp: string;
  newBalance?: number;
  description: string;
}

interface KioskState {
  // Kiosk identity (set from env at startup)
  kioskId: string;

  // Active visitor session
  visitorInfo: VisitorInfo | null;

  // Shopping cart for attraction tickets
  cartItems: CartItem[];

  // UI language
  language: Locale;

  // Last completed transaction (for success screen)
  lastReceipt: TransactionReceipt | null;

  // F&B ordering state
  fnbOutletId: string | null;
  fnbCart: FnbCartItem[];
  /** Allergen codes to filter out — items containing these are hidden */
  fnbAllergenFilter: string[];
  fnbCurrentOrder: FnbOrderResult | null;

  // Information kiosk mode (device type = INFORMATION)
  isInfoMode: boolean;

  // Photo kiosk state
  photoWristbandId: string | null;
  photos: KioskPhoto[];
  photoCart: string[];

  // Actions
  setKioskId: (id: string) => void;
  setVisitorInfo: (info: VisitorInfo | null) => void;
  updateCreditBalance: (newBalance: number) => void;
  addToCart: (item: CartItem) => void;
  removeFromCart: (attractionId: string) => void;
  clearCart: () => void;
  setLanguage: (lang: Locale) => void;
  toggleLanguage: () => void;
  setLastReceipt: (receipt: TransactionReceipt | null) => void;
  endSession: () => void;

  // F&B actions
  setFnbOutletId: (outletId: string | null) => void;
  addToFnbCart: (item: FnbCartItem) => void;
  removeFromFnbCart: (menuItemId: string, variantId?: string) => void;
  updateFnbCartItemQty: (menuItemId: string, quantity: number, variantId?: string) => void;
  clearFnbCart: () => void;
  toggleAllergenFilter: (allergen: string) => void;
  setFnbOrder: (order: FnbOrderResult | null) => void;

  // Info mode action
  setInfoMode: (enabled: boolean) => void;

  // Photo kiosk actions
  setPhotoWristband: (wristbandId: string | null) => void;
  setPhotos: (photos: KioskPhoto[]) => void;
  togglePhotoSelection: (photoId: string) => void;
  clearPhotoCart: () => void;
}

export const useKioskStore = create<KioskState>((set, _get) => ({
  kioskId: localStorage.getItem('kiosk_staff_id') ?? '',
  visitorInfo: ((window as unknown as Record<string, unknown>)._e2eVisitorInfo as VisitorInfo | null) ?? null,
  cartItems: [],
  language: 'id',
  lastReceipt: null,

  // F&B initial state
  fnbOutletId: null,
  fnbCart: [],
  fnbAllergenFilter: [],
  fnbCurrentOrder: null,

  // Info mode (false = standard kiosk, true = information kiosk)
  isInfoMode: false,

  // Photo kiosk initial state
  photoWristbandId: null,
  photos: [],
  photoCart: [],

  setKioskId: (id) => set({ kioskId: id }),

  setVisitorInfo: (info) => set({ visitorInfo: info }),

  updateCreditBalance: (newBalance) =>
    set((state) => ({
      visitorInfo: state.visitorInfo
        ? { ...state.visitorInfo, creditBalance: newBalance }
        : null,
    })),

  addToCart: (item) =>
    set((state) => {
      // Only allow one of each attraction per cart
      const exists = state.cartItems.some((c) => c.attractionId === item.attractionId);
      if (exists) return state;
      return { cartItems: [...state.cartItems, item] };
    }),

  removeFromCart: (attractionId) =>
    set((state) => ({
      cartItems: state.cartItems.filter((c) => c.attractionId !== attractionId),
    })),

  clearCart: () => set({ cartItems: [] }),

  setLanguage: (lang) => set({ language: lang }),

  toggleLanguage: () =>
    set((state) => {
      const order: Locale[] = ['id', 'en'];
      const idx = order.indexOf(state.language);
      return { language: order[(idx + 1) % order.length] };
    }),

  setLastReceipt: (receipt) => set({ lastReceipt: receipt }),

  endSession: () =>
    set({
      visitorInfo: null,
      cartItems: [],
      lastReceipt: null,
      fnbOutletId: null,
      fnbCart: [],
      fnbAllergenFilter: [],
      fnbCurrentOrder: null,
    }),

  // F&B actions
  setFnbOutletId: (outletId) => set({ fnbOutletId: outletId }),

  addToFnbCart: (item) =>
    set((state) => {
      // Normalise variantId to '' so undefined and missing string both match the same slot
      const itemVariantKey = item.variantId ?? '';
      const existingIndex = state.fnbCart.findIndex(
        (c) => c.menuItemId === item.menuItemId && (c.variantId ?? '') === itemVariantKey,
      );
      const existing = existingIndex >= 0 ? state.fnbCart[existingIndex] : undefined;
      if (existing) {
        // Increment quantity for same item+variant combination
        const updated = [...state.fnbCart];
        updated[existingIndex] = { ...existing, quantity: existing.quantity + item.quantity };
        return { fnbCart: updated };
      }
      return { fnbCart: [...state.fnbCart, item] };
    }),

  removeFromFnbCart: (menuItemId, variantId?) =>
    set((state) => {
      const targetVariantKey = variantId ?? '';
      return {
        fnbCart: state.fnbCart.filter(
          (c) => !(c.menuItemId === menuItemId && (c.variantId ?? '') === targetVariantKey),
        ),
      };
    }),

  updateFnbCartItemQty: (menuItemId, quantity, variantId?) =>
    set((state) => {
      const targetVariantKey = variantId ?? '';
      if (quantity <= 0) {
        return {
          fnbCart: state.fnbCart.filter(
            (c) => !(c.menuItemId === menuItemId && (c.variantId ?? '') === targetVariantKey),
          ),
        };
      }
      return {
        fnbCart: state.fnbCart.map((c) =>
          c.menuItemId === menuItemId && (c.variantId ?? '') === targetVariantKey
            ? { ...c, quantity }
            : c,
        ),
      };
    }),

  clearFnbCart: () => set({ fnbCart: [] }),

  toggleAllergenFilter: (allergen) =>
    set((state) => {
      const active = state.fnbAllergenFilter.includes(allergen);
      return {
        fnbAllergenFilter: active
          ? state.fnbAllergenFilter.filter((a) => a !== allergen)
          : [...state.fnbAllergenFilter, allergen],
      };
    }),

  setFnbOrder: (order) => set({ fnbCurrentOrder: order }),

  // Info mode
  setInfoMode: (enabled) => set({ isInfoMode: enabled }),

  // Photo kiosk actions
  setPhotoWristband: (wristbandId) => set({ photoWristbandId: wristbandId }),

  setPhotos: (photos) => set({ photos }),

  togglePhotoSelection: (photoId) =>
    set((state) => {
      const alreadySelected = state.photoCart.includes(photoId);
      return {
        photoCart: alreadySelected
          ? state.photoCart.filter((id) => id !== photoId)
          : [...state.photoCart, photoId],
      };
    }),

  clearPhotoCart: () => set({ photoCart: [], photoWristbandId: null, photos: [] }),
}));

import { cartCount, cartTotal } from '../shared/cart/selectors';

/** Derived selector: total credits in cart (legacy attraction cart, uses creditCost). */
export const selectCartTotal = (state: KioskState): number =>
  state.cartItems.reduce((sum, item) => sum + item.creditCost, 0);

/** Derived selector: F&B cart total in IDR. */
export const selectFnbTotal = (state: KioskState): number => cartTotal(state.fnbCart);

/** Derived selector: F&B cart item count. */
export const selectFnbCount = (state: KioskState): number => cartCount(state.fnbCart);
